import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, euro, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LENGTHS = ["Corta", "Media", "Lunga", "Extra Lunga"];
const SHAPES = ["Mandorla", "Quadrata", "Ovale", "Ballerina", "Stiletto"];
const CONDITIONS = ["Nessun prodotto", "Semipermanente", "Gel / ricostruzione", "Prodotto di altro salone", "Unghie rotte"];

export default function Booking() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [addons, setAddons] = useState([]);
  const [look, setLook] = useState(null);
  const [sel, setSel] = useState({ service_id: params.get("service") || "", addon_ids: [], length: "Media", shape: "Mandorla", current_condition: "Nessun prodotto", artist_id: "any", date: "", start_time: "" });
  const [avail, setAvail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [wallet, setWallet] = useState(null);
  const [usePoints, setUsePoints] = useState(false);
  const [useCredit, setUseCredit] = useState(false);
  const [giftCode, setGiftCode] = useState("");
  const [giftInfo, setGiftInfo] = useState(null);

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/addons").then((r) => setAddons(r.data)).catch(() => {});
    if (user) api.get("/my/wallet").then((r) => setWallet(r.data)).catch(() => {});
    const lookId = params.get("look");
    if (lookId) api.get("/gallery").then((r) => {
      const l = r.data.find((x) => x.id === lookId);
      if (l) { setLook(l); setSel((p) => ({ ...p, service_id: p.service_id || l.service_id || l.category_id })); }
    }).catch(() => {});
  }, [user]);

  const service = services.find((s) => s.id === sel.service_id);
  const selectedAddons = addons.filter((a) => sel.addon_ids.includes(a.id));
  const lengthExtra = { Corta: 0, Media: 0, Lunga: 5, "Extra Lunga": 10 }[sel.length];
  const price = (service?.base_price || 0) + selectedAddons.reduce((s, a) => s + a.price, 0) + lengthExtra;
  const duration = (service?.duration_min || 0) + selectedAddons.reduce((s, a) => s + a.duration_min, 0) + { Corta: 0, Media: 0, Lunga: 10, "Extra Lunga": 20 }[sel.length];

  const toggleAddon = (id) => setSel((p) => ({ ...p, addon_ids: p.addon_ids.includes(id) ? p.addon_ids.filter((x) => x !== id) : [...p.addon_ids, id] }));

  const loadAvail = async (date) => {
    setSel((p) => ({ ...p, date, start_time: "" }));
    const { data } = await api.post("/availability", { service_id: sel.service_id, addon_ids: sel.addon_ids, artist_id: sel.artist_id, date });
    setAvail(data);
  };

  const dates = useMemo(() => {
    const out = []; const d = new Date();
    for (let i = 1; i <= 14; i++) { const n = new Date(d); n.setDate(d.getDate() + i); out.push(n.toISOString().slice(0, 10)); }
    return out;
  }, []);

  const deposit = Math.round(price * 0.3 * 100) / 100;
  const rate = wallet?.points_to_eur || 0.01;
  const usableEur = wallet?.usable_eur || 0;
  const effectivePolicy = service?.non_derogable ? "required" : (user && ["required", "waived"].includes(user.deposit_policy) ? user.deposit_policy : "standard");
  const isWaived = effectivePolicy === "waived";
  const pointsEurApplied = usePoints ? Math.min(usableEur, deposit) : 0;
  const giftApplied = giftInfo?.valid ? Math.min(giftInfo.balance_eur, Math.max(0, Math.round((deposit - pointsEurApplied) * 100) / 100)) : 0;
  const creditBalance = wallet?.credit?.balance || 0;
  const creditApplied = useCredit ? Math.min(creditBalance, Math.max(0, Math.round((deposit - pointsEurApplied - giftApplied) * 100) / 100)) : 0;
  const remainingToPay = Math.max(0, Math.round((deposit - pointsEurApplied - giftApplied - creditApplied) * 100) / 100);
  const pointsToUse = usePoints ? Math.min(wallet?.points || 0, Math.ceil(deposit / rate)) : 0;

  const applyGift = async () => {
    if (!giftCode.trim()) return;
    try {
      const { data } = await api.get(`/gift-cards/${giftCode.trim().toUpperCase()}`);
      if (!data.valid) { toast.error("Buono regalo non valido o esaurito"); setGiftInfo(null); return; }
      setGiftInfo(data); toast.success(`Buono applicato · saldo ${euro(data.balance_eur)}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Codice non trovato"); setGiftInfo(null); }
  };

  const confirm = async () => {
    if (!user) { toast.error("Accedi per completare la prenotazione"); nav("/accedi?return=/prenota"); return; }
    setLoading(true);
    try {
      const slot = avail.slots.find((s) => s.start_time === sel.start_time);
      const { data: booking } = await api.post("/bookings", {
        service_id: sel.service_id, addon_ids: sel.addon_ids, artist_id: slot.artist_id,
        date: sel.date, start_time: sel.start_time, length: sel.length, shape: sel.shape,
        current_condition: sel.current_condition, inspiration_image: look?.image || null, source: params.get("look") ? "galleria" : params.get("match") ? "kima-match" : "servizio",
      });
      if (booking.deposit_waived) {
        nav(`/prenota/conferma?booking=${booking.id}&waived=1`);
        return;
      }
      const { data: co } = await api.post("/payments/checkout", { booking_id: booking.id, origin_url: window.location.origin, payment_type: "deposit", points_to_use: pointsToUse, gift_card_code: giftInfo?.valid ? giftInfo.code : null, use_credit: useCredit });
      if (co.confirmed) {
        nav(`/prenota/conferma?booking=${booking.id}&points=1`);
        return;
      }
      window.location.href = co.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore nella prenotazione");
      setLoading(false);
    }
  };

  const Step = ({ n, label }) => (
    <div className={`flex items-center gap-2 ${step >= n ? "text-espresso" : "text-taupe"}`}>
      <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs border ${step > n ? "bg-espresso text-ivory border-espresso" : step === n ? "border-burgundy text-burgundy" : "border-taupe"}`}>{step > n ? <Check size={14} /> : n}</div>
      <span className="text-sm hidden md:inline">{label}</span>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
      <h1 className="font-serif text-4xl md:text-5xl text-espresso mb-8">Prenota</h1>
      <div className="flex items-center justify-between mb-12 gap-2 overflow-x-auto">
        <Step n={1} label="Servizio" /><div className="flex-1 h-px bg-softgrey mx-2" />
        <Step n={2} label="Condizione" /><div className="flex-1 h-px bg-softgrey mx-2" />
        <Step n={3} label="Configura" /><div className="flex-1 h-px bg-softgrey mx-2" />
        <Step n={4} label="Artista" /><div className="flex-1 h-px bg-softgrey mx-2" />
        <Step n={5} label="Data & ora" /><div className="flex-1 h-px bg-softgrey mx-2" />
        <Step n={6} label="Pagamento" />
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 animate-fade-up">
          {step === 1 && (
            <div className="grid sm:grid-cols-2 gap-3">
              {services.map((s) => (
                <button key={s.id} onClick={() => setSel((p) => ({ ...p, service_id: s.id }))} data-testid={`sel-service-${s.id}`} className={`text-left border rounded-sm p-5 transition-colors ${sel.service_id === s.id ? "border-burgundy bg-offwhite" : "border-espresso/15 hover:border-espresso"}`}>
                  <div className="font-serif text-xl text-espresso">{s.name}</div>
                  <div className="text-sm text-espresso/60 flex gap-3 mt-1"><span>{s.from_price ? "da " : ""}{euro(s.base_price)}</span><span>{s.duration_min}min</span></div>
                </button>
              ))}
            </div>
          )}
          {step === 2 && (
            <div className="grid gap-3">
              {CONDITIONS.map((c) => (
                <button key={c} onClick={() => setSel((p) => ({ ...p, current_condition: c }))} data-testid={`sel-cond-${c}`} className={`text-left border rounded-sm px-6 py-4 transition-colors ${sel.current_condition === c ? "border-burgundy bg-offwhite" : "border-espresso/15 hover:border-espresso"}`}>{c}</button>
              ))}
            </div>
          )}
          {step === 3 && (
            <div className="space-y-8">
              <div><div className="kima-overline text-taupe mb-3">Lunghezza</div><div className="flex flex-wrap gap-2">{LENGTHS.map((l) => (<button key={l} onClick={() => setSel((p) => ({ ...p, length: l }))} data-testid={`sel-len-${l}`} className={`px-4 py-2 rounded-sm text-sm border transition-colors ${sel.length === l ? "bg-espresso text-ivory border-espresso" : "border-espresso/20 hover:border-espresso"}`}>{l}</button>))}</div></div>
              <div><div className="kima-overline text-taupe mb-3">Forma</div><div className="flex flex-wrap gap-2">{SHAPES.map((l) => (<button key={l} onClick={() => setSel((p) => ({ ...p, shape: l }))} data-testid={`sel-shape-${l}`} className={`px-4 py-2 rounded-sm text-sm border transition-colors ${sel.shape === l ? "bg-espresso text-ivory border-espresso" : "border-espresso/20 hover:border-espresso"}`}>{l}</button>))}</div></div>
              <div><div className="kima-overline text-taupe mb-3">Extra & add-on</div><div className="grid sm:grid-cols-2 gap-2">{addons.map((a) => (<button key={a.id} onClick={() => toggleAddon(a.id)} data-testid={`sel-addon-${a.id}`} className={`flex justify-between items-center border rounded-sm px-4 py-3 text-sm transition-colors ${sel.addon_ids.includes(a.id) ? "border-burgundy bg-offwhite" : "border-espresso/15 hover:border-espresso"}`}><span>{a.name}</span><span className="text-espresso/60">+{euro(a.price)}</span></button>))}</div></div>
            </div>
          )}
          {step === 4 && (
            <div className="grid sm:grid-cols-2 gap-3">
              <button onClick={() => setSel((p) => ({ ...p, artist_id: "any" }))} data-testid="sel-artist-any" className={`text-left border rounded-sm p-5 transition-colors ${sel.artist_id === "any" ? "border-burgundy bg-offwhite" : "border-espresso/15 hover:border-espresso"}`}><div className="font-serif text-xl text-espresso">Prima disponibile</div><div className="text-sm text-espresso/60">KIMA sceglie l'artista qualificata</div></button>
              <ArtistPicker sel={sel} setSel={setSel} categoryId={service?.category_id} />
            </div>
          )}
          {step === 5 && (
            <div>
              <div className="kima-overline text-taupe mb-3">Scegli il giorno</div>
              <div className="flex flex-wrap gap-2 mb-8">
                {dates.map((d) => (<button key={d} onClick={() => loadAvail(d)} data-testid={`sel-date-${d}`} className={`px-3 py-2 rounded-sm text-sm border transition-colors ${sel.date === d ? "bg-espresso text-ivory border-espresso" : "border-espresso/20 hover:border-espresso"}`}>{fmtDate(d).slice(0, 5)}</button>))}
              </div>
              {avail && (avail.slots.length ? (
                <><div className="kima-overline text-taupe mb-3">Orari disponibili</div>
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">{avail.slots.map((s) => (<button key={s.start_time + s.artist_id} onClick={() => setSel((p) => ({ ...p, start_time: s.start_time }))} data-testid={`sel-slot-${s.start_time}`} className={`px-3 py-3 rounded-sm text-sm border transition-colors ${sel.start_time === s.start_time ? "bg-burgundy text-ivory border-burgundy" : "border-espresso/20 hover:border-espresso"}`}>{s.start_time}</button>))}</div></>
              ) : (<p className="text-espresso/60 border border-espresso/10 p-6 rounded-sm">Nessuno slot disponibile in questa data. Prova un altro giorno.</p>))}
            </div>
          )}
          {step === 6 && (
            <div className="border border-espresso/10 bg-offwhite rounded-sm p-8">
              {!user && <p className="text-kimaerror text-sm mb-4" data-testid="booking-login-hint">Accedi o registrati per confermare la prenotazione.</p>}
              {isWaived ? (
                <>
                  <h3 className="font-serif text-2xl text-espresso mb-3">Prenotazione immediata</h3>
                  <div className="border border-kimasuccess/30 bg-kimasuccess/5 rounded-sm p-4 mb-6" data-testid="waived-notice">
                    <p className="text-espresso text-sm">Per il tuo profilo non è richiesto alcun acconto. La prenotazione sarà confermata subito dopo la verifica dello slot.</p>
                  </div>
                  <button onClick={confirm} disabled={loading} data-testid="booking-confirm-waived" className="w-full bg-espresso text-ivory py-4 rounded-sm hover:bg-burgundy transition-colors disabled:opacity-50">{loading ? "Attendere…" : "Conferma prenotazione"}</button>
                </>
              ) : (
                <>
                  <h3 className="font-serif text-2xl text-espresso mb-2">Acconto richiesto</h3>
                  <p className="text-espresso/70 mb-6 text-sm">La prenotazione sarà confermata dopo il completamento dell'acconto (30%). Il saldo verrà pagato in salone.{service?.non_derogable && " Questo servizio richiede sempre l'acconto."}</p>

                  {user && wallet && (wallet.points || 0) > 0 && (
                    <div className="border border-espresso/10 rounded-sm p-4 mb-5" data-testid="points-box">
                      <label className="flex items-center justify-between gap-3 cursor-pointer">
                        <span className="text-sm text-espresso">Usa i miei punti KIMA<br /><span className="text-xs text-taupe">{wallet.points} punti · valore {euro(usableEur)}</span></span>
                        <input type="checkbox" data-testid="use-points" checked={usePoints} onChange={(e) => setUsePoints(e.target.checked)} className="w-5 h-5 accent-burgundy" />
                      </label>
                    </div>
                  )}

                  {user && wallet && (wallet.credit?.balance || 0) > 0 && (
                    <div className="border border-espresso/10 rounded-sm p-4 mb-5" data-testid="credit-box">
                      <label className="flex items-center justify-between gap-3 cursor-pointer">
                        <span className="text-sm text-espresso">Usa il mio KIMA Credit<br /><span className="text-xs text-taupe">saldo disponibile {euro(wallet.credit.balance)}</span></span>
                        <input type="checkbox" data-testid="use-credit" checked={useCredit} onChange={(e) => setUseCredit(e.target.checked)} className="w-5 h-5 accent-burgundy" />
                      </label>
                    </div>
                  )}

                  <div className="border border-espresso/10 rounded-sm p-4 mb-5" data-testid="gift-box">
                    <label className="text-sm text-espresso block mb-2">Hai un buono regalo?</label>
                    <div className="flex gap-2">
                      <input value={giftCode} data-testid="booking-gift-code" onChange={(e) => setGiftCode(e.target.value)} placeholder="KIMA-XXXX-XXXX" className="flex-1 border border-espresso/20 rounded-sm px-3 py-2 text-sm uppercase bg-ivory" />
                      <button type="button" onClick={applyGift} data-testid="booking-gift-apply" className="bg-espresso text-ivory px-4 py-2 rounded-sm text-sm hover:bg-burgundy transition-colors">Applica</button>
                    </div>
                    {giftInfo?.valid && <p className="text-xs text-kimasuccess mt-2" data-testid="gift-valid">Buono valido · saldo {euro(giftInfo.balance_eur)}</p>}
                  </div>

                  <dl className="space-y-2 text-sm border-t border-espresso/10 pt-4 mb-6">
                    <div className="flex justify-between"><dt className="text-espresso/60">Acconto richiesto</dt><dd className="text-espresso">{euro(deposit)}</dd></div>
                    {usePoints && <div className="flex justify-between text-burgundy"><dt>Pagato con punti</dt><dd data-testid="points-applied">− {euro(pointsEurApplied)}</dd></div>}
                    {giftApplied > 0 && <div className="flex justify-between text-burgundy"><dt>Buono regalo</dt><dd data-testid="gift-applied">− {euro(giftApplied)}</dd></div>}
                    {creditApplied > 0 && <div className="flex justify-between text-burgundy"><dt>KIMA Credit</dt><dd data-testid="credit-applied">− {euro(creditApplied)}</dd></div>}
                    <div className="flex justify-between font-medium"><dt className="text-espresso">Da pagare con carta</dt><dd className="text-espresso" data-testid="remaining-to-pay">{euro(remainingToPay)}</dd></div>
                  </dl>

                  <button onClick={confirm} disabled={loading} data-testid="booking-pay" className="w-full bg-espresso text-ivory py-4 rounded-sm hover:bg-burgundy transition-colors disabled:opacity-50">
                    {loading ? "Attendere…" : remainingToPay <= 0 ? "Conferma prenotazione" : `Paga acconto ${euro(remainingToPay)}`}
                  </button>
                  <p className="text-xs text-espresso/40 mt-3">Pagamento sicuro in modalità test Stripe. Nessun punto viene generato sulla parte pagata con punti.</p>
                </>
              )}
            </div>
          )}

          <div className="flex justify-between mt-10">
            <button onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} className="text-sm text-taupe hover:text-espresso transition-colors disabled:opacity-0">← Indietro</button>
            {step < 6 && <button onClick={() => setStep(step + 1)} disabled={(step === 1 && !sel.service_id) || (step === 5 && !sel.start_time)} data-testid="booking-next" className="bg-espresso text-ivory px-8 py-3 rounded-sm hover:bg-burgundy transition-colors disabled:opacity-40 text-sm">Continua</button>}
          </div>
        </div>

        {/* Summary */}
        <aside className="border border-espresso/10 bg-offwhite rounded-sm p-6 h-fit lg:sticky lg:top-24">
          <div className="kima-overline text-burgundy mb-4">Riepilogo</div>
          {look && <img src={look.image} alt="look" className="w-full aspect-video object-cover rounded-sm mb-4" />}
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between"><dt className="text-espresso/60">Servizio</dt><dd className="text-espresso text-right">{service?.name || "—"}</dd></div>
            <div className="flex justify-between"><dt className="text-espresso/60">Lunghezza</dt><dd className="text-espresso">{sel.length}</dd></div>
            <div className="flex justify-between"><dt className="text-espresso/60">Forma</dt><dd className="text-espresso">{sel.shape}</dd></div>
            {selectedAddons.map((a) => (<div key={a.id} className="flex justify-between"><dt className="text-espresso/60">{a.name}</dt><dd className="text-espresso">+{euro(a.price)}</dd></div>))}
            {sel.date && <div className="flex justify-between"><dt className="text-espresso/60">Data</dt><dd className="text-espresso">{fmtDate(sel.date)} {sel.start_time}</dd></div>}
          </dl>
          <div className="border-t border-espresso/10 mt-5 pt-5 space-y-2">
            <div className="flex justify-between text-sm text-espresso/70"><span className="flex items-center gap-1"><Clock size={14} /> Durata</span><span>{duration} min</span></div>
            <div className="flex justify-between text-espresso"><span>Totale stimato</span><span className="font-medium">{euro(price)}</span></div>
            <div className="flex justify-between text-burgundy text-sm"><span>Acconto (30%)</span><span>{euro(price * 0.3)}</span></div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function ArtistPicker({ sel, setSel, categoryId }) {
  const [staff, setStaff] = useState([]);
  useEffect(() => { api.get("/staff").then((r) => setStaff(r.data)).catch(() => {}); }, []);
  const qualified = staff.filter((s) => !categoryId || s.skills.includes(categoryId));
  return (<>{qualified.map((a) => (
    <button key={a.id} onClick={() => setSel((p) => ({ ...p, artist_id: a.id }))} data-testid={`sel-artist-${a.id}`} className={`text-left border rounded-sm p-5 transition-colors ${sel.artist_id === a.id ? "border-burgundy bg-offwhite" : "border-espresso/15 hover:border-espresso"}`}><div className="font-serif text-xl text-espresso">{a.name}</div><div className="text-sm text-espresso/60">{a.role_title}</div></button>
  ))}</>);
}
